import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import NewsSection from "./components/News Page/NewsSection";
import CommunitySection from "./components/Community Page/CommunitySection";
import AboutPage from "./pages/AboutPage";
import NewsPage from "./pages/NewsPage";
import CommunityPage from "./pages/CommunityPage";
import CareerPage from "./pages/CareerPage";
import CommunityEventTemplate from "./components/Community Page/CommunityEventPage";
import Footer from "./components/Footer";

const routeNames = {
  '/': 'Home',
  '/news': 'News',
  '/community': 'Community',
  '/career': 'Career'
};

const routes = ['/', '/news', '/community', '/career'];

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Update currentIndex when location changes
  useEffect(() => {
    const index = routes.indexOf(location.pathname);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  }, [location]);

  // Only allow swipe navigation on main routes
  const isMainRoute = routes.includes(location.pathname);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isMainRoute) return;
      if (currentIndex < routes.length - 1) {
        setDirection(1);
        navigate(routes[currentIndex + 1]);
      }
    },
    onSwipedRight: () => {
      if (!isMainRoute) return;
      if (currentIndex > 0) {
        setDirection(-1);
        navigate(routes[currentIndex - 1]);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    trackTouch: true,
    delta: 50, // Minimum swipe distance
    swipeDuration: 500, // Maximum time for swipe gesture
    touchEventOptions: { passive: false }
  });

  const pageTransition = {
    initial: {
      opacity: 0,
      x: direction * 300
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: direction * -300
    },
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  };

  const nextIndex = currentIndex + 1;
  const nextRoute = nextIndex < routes.length ? routes[nextIndex] : null;

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      <Navbar />
      
      {/* Main content with swipe handlers */}
      <div className="flex-grow" {...handlers}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            {...pageTransition}
            className="h-full"
          >
            <Routes location={location}>
              <Route path="/" element={
                <div className="pt-20">
                  <HeroSection />
                  <NewsSection />
                  <CommunitySection />
                </div>
              } />
              <Route path="about" element={<AboutPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/:slug" element={<CommunityEventTemplate />} />
              <Route path="/career" element={<CareerPage />} />
            </Routes>
          </motion.div>
        </AnimatePresence>

        {/* Navigation indicators */}
        {isMainRoute && nextRoute && (
          <>
            {/* Desktop indicator */}
            <div className="hidden md:flex items-center gap-3 p-2 rounded-md bg-white/20 backdrop-blur-md text-white fixed bottom-4 right-4 shadow-lg z-50">
              <span className="text-sm md:text-base"></span>
              <span className="font-bold">{routeNames[nextRoute]}</span>
              <svg
                className="w-5 h-5 md:w-6 md:h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            </div>

            {/* Mobile indicator */}
            <div className="md:hidden flex items-center gap-2 justify-center p-2 bg-white/20 backdrop-blur-md text-white fixed bottom-4 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg z-50">
              <span className="text-xs">← Swipe →</span>
              <span className="text-xs">
                Next: {routeNames[nextRoute]}
              </span>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AppContent;